import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Clock, Play, Pause, AlertTriangle, CheckCircle2, Flame, Trophy, Timer, User, ChevronRight, VolumeX } from 'lucide-react';
import { getClimbByCompetition, getCompetitionById, getTimerState, getCompetitionGroups } from '../services/apiServices';
import { slugToId } from '../util/slug';
import { AudioManager } from './AudioManager';

export const GroupCompetitionTimer = () => {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const [group, setGroup] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [competitions, setCompetitions] = useState([]);
    const [allClimbData, setAllClimbData] = useState({});
    const [currentTime, setCurrentTime] = useState(new Date());
    const [competition, setCompetition] = useState(null);
    const [timerState, setTimerState] = useState(null);
    const [localRemainingTime, setLocalRemainingTime] = useState(0);
    const [climbData, setClimbData] = useState([]);
    const [currentRound, setCurrentRound] = useState(1);
    const [allClimbers, setAllClimbers] = useState([]);
    const [currentRoundClimbers, setCurrentRoundClimbers] = useState([]);
    const [nextRoundClimbers, setNextRoundClimbers] = useState([]);
    const [activeCompetitionIndex, setActiveCompetitionIndex] = useState(0);
    const [lastSyncTime, setLastSyncTime] = useState(null);
    const [showAudioPrompt, setShowAudioPrompt] = useState(true);

    console.log(timerState, 'timerState');
    console.log('Local remaining time:', localRemainingTime);

    // Refs
    const isMountedRef = useRef(true);
    const timerIntervalRef = useRef(null);
    const lastTimerStateRef = useRef(null);
    const pollingIntervalRef = useRef(null);
    const climbPollingIntervalRef = useRef(null);
    const lastSyncRef = useRef(Date.now());

    // Helper function to get phase duration
    const getPhaseDuration = useCallback((phase, comp) => {
        if (!phase || !comp) return 0;

        switch (phase) {
            case 'countdown':
                return comp.observationTime || 180;
            case 'climbing':
                return comp.climbingTime || 240;
            case 'wrap_time':
                return comp.wrapTime || 30;
            case 'warning':
                return comp.warningTime || 60;
            default:
                return 0;
        }
    }, []);

    // Compute remaining time from end time
    const computeRemainingFromEndTime = useCallback((timer) => {
        if (!timer?.currentEndTime) {
            return 0;
        }

        try {
            const now = Date.now();
            const end = new Date(timer.currentEndTime).getTime();

            if (isNaN(end)) {
                return 0;
            }

            const diffInMs = end - now;
            const diffInSeconds = Math.floor(diffInMs / 1000);

            return Math.max(0, diffInSeconds);
        } catch (error) {
            console.error('Error computing remaining time:', error);
            return 0;
        }
    }, []);

    // Handle user interaction for audio
    const handleUserInteraction = () => {
        setShowAudioPrompt(false);
    };

    // Stop local timer
    const stopLocalTimer = useCallback(() => {
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }
    }, []);

    // Start local countdown timer
    const startLocalTimer = useCallback(() => {
        stopLocalTimer();

        let lastUpdateTime = Date.now();

        timerIntervalRef.current = setInterval(() => {
            const now = Date.now();
            const elapsed = now - lastUpdateTime;

            // Update approximately every second (allowing for some drift)
            if (elapsed >= 900) {
                lastUpdateTime = now;

                setLocalRemainingTime(prevTime => {
                    if (prevTime <= 0) {
                        stopLocalTimer();
                        return 0;
                    }

                    const newTime = Math.max(0, prevTime - 1);

                    if (newTime === 0 && prevTime > 0) {
                        console.log('⏰ Local countdown reached zero');
                        // Don't stop timer here - server will update state
                    }

                    return newTime;
                });
            }
        }, 100); // Check more frequently for better responsiveness
    }, [stopLocalTimer]);

    // Calculate score for a specific competition
    const calculateScore = useCallback((climb, comp) => {
        if (!climb) return 0;

        let zonePoints = 0;
        let topPoints = 0;
        const Zone = comp?.zonepoints || 10;
        const Top = comp?.toppoints || 25;
        const penalty = comp?.penalty || 0.1;

        if (climb.zoneReached && climb.zoneAttempt) {
            zonePoints = Zone - (climb.zoneAttempt - 1) * penalty;
        }

        if (climb.topReached && climb.topAttempt) {
            topPoints = Top - (climb.topAttempt - 1) * penalty;
        }

        return Math.max(zonePoints, topPoints);
    }, []);

    // Main function to fetch timer state via polling
    const fetchTimerState = useCallback(async () => {
        try {
            if (!competition?._id) return;

            console.log('🔄 Polling timer state...');
            const response = await getTimerState(competition._id);
            if (response && response !== "false") {
                const data = await response.json();
                if (data.success && data.data) {
                    const timerData = data.data;

                    // Update last sync time
                    setLastSyncTime(new Date().toISOString());
                    lastSyncRef.current = Date.now();

                    // Store previous phase for transition detection
                    const previousPhase = lastTimerStateRef.current?.currentPhase;
                    lastTimerStateRef.current = timerData;

                    // Update timer state
                    setTimerState(timerData);

                    // Update current round
                    if (timerData.currentRound) {
                        setCurrentRound(timerData.currentRound);
                    }

                    if (timerData.status === 'paused') {
                        setLocalRemainingTime(0);
                        stopLocalTimer();
                        return;
                    }

                    // Calculate remaining time from end time
                    const computedRemaining = computeRemainingFromEndTime(timerData);

                    // Check if this is a phase transition
                    const isPhaseTransition = previousPhase &&
                        previousPhase !== timerData.currentPhase;

                    console.log('Transition check:', {
                        previousPhase,
                        currentPhase: timerData.currentPhase,
                        computedRemaining,
                        isPhaseTransition,
                        currentLocalTime: localRemainingTime
                    });

                    // Always update the remaining time from server
                    if (computedRemaining > 0) {
                        console.log(`Using computed remaining time from server: ${computedRemaining}s`);
                        setLocalRemainingTime(computedRemaining);

                    } else if (isPhaseTransition && timerData.currentPhase && timerData.currentPhase !== 'completed') {
                        // During phase transition, show full duration of the new phase
                        const phaseDuration = getPhaseDuration(timerData.currentPhase, competition);
                        if (phaseDuration > 0) {
                            console.log(`🔄 Phase transition detected (${previousPhase} → ${timerData.currentPhase}). Setting initial time: ${phaseDuration}s`);
                            setLocalRemainingTime(phaseDuration);
                        }
                    } else if (timerData.remainingTime && timerData.remainingTime > 0) {
                        // Fallback to server's remaining time
                        console.log(`Using server remaining time: ${timerData.remainingTime}s`);
                        setLocalRemainingTime(timerData.remainingTime);
                    }

                    // Manage local timer based on new state
                    const { status, currentPhase } = timerData;

                    // Check if we should be running
                    const shouldBeRunning = timerData.currentEndTime &&
                        (status === 'running' ||
                            ['countdown', 'climbing', 'wrap_time', 'warning'].includes(currentPhase));

                    if (shouldBeRunning) {
                        const hasTimeRemaining = computedRemaining > 0 || localRemainingTime > 0;

                        if (!timerIntervalRef.current && hasTimeRemaining) {
                            console.log('▶️ Starting local countdown for:', status || currentPhase);
                            startLocalTimer();
                        } else if (timerIntervalRef.current && computedRemaining > 0) {
                            // Keep timer running, sync time if needed
                            const timeDiff = Math.abs(computedRemaining - localRemainingTime);
                            if (timeDiff > 2) {
                                console.log(`🔄 Syncing timer: local=${localRemainingTime}, server=${computedRemaining}`);
                                setLocalRemainingTime(computedRemaining);
                            }
                        }
                    } else {
                        // Not an active phase, stop timer
                        stopLocalTimer();
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching timer state:', error);
        }
    }, [competition, stopLocalTimer, computeRemainingFromEndTime, getPhaseDuration, startLocalTimer, localRemainingTime]);

    // Fetch climb data for all competitions
    const fetchClimbData = useCallback(async (competitionsList) => {
        try {
            const climbPromises = competitionsList.map(async (comp) => {
                try {
                    const response = await getClimbByCompetition(comp._id);
                    if (response && response !== "false") {
                        const climbData = await response.json();
                        return { competitionId: comp._id, data: climbData };
                    }
                } catch (error) {
                    console.error(`Error fetching climb data for ${comp._id}:`, error);
                }
                return { competitionId: comp._id, data: [] };
            });

            const climbResults = await Promise.all(climbPromises);
            const climbDataMap = {};
            climbResults.forEach(result => {
                climbDataMap[result.competitionId] = result.data;
            });
            setAllClimbData(climbDataMap);

            // Flatten climb data for all competitions
            const allData = climbResults.flatMap(result => result.data || []);
            setClimbData(allData);

        } catch (error) {
            console.error("Error fetching climb data:", error);
        }
    }, []);

    // Fetch group and competition data
    useEffect(() => {
        async function fetchGroupData() {
            if (!groupId) return;

            try {
                setIsLoading(true);

                const loggedAdminInfo = localStorage.getItem("loggedAdminInfo");
                if (!loggedAdminInfo) {
                    navigate('/login');
                    return;
                }

                const organizerInfo = JSON.parse(loggedAdminInfo);
                const organizerId = organizerInfo?.organizerId;

                const groupsResponse = await getCompetitionGroups(organizerId);
                if (!groupsResponse?.ok) return;

                const groupsData = await groupsResponse.json();
                if (!groupsData.success) return;

                const foundGroup = groupsData.data.find(g => g._id === groupId);
                if (!foundGroup) return;

                setGroup(foundGroup);

                // Fetch all competitions in the group
                const compPromises = foundGroup.competitionIds.map(async (compId) => {
                    try {
                        const response = await getCompetitionById(compId);
                        if (response?.ok) {
                            return await response.json();
                        }
                    } catch (error) {
                        console.error(`Error fetching competition ${compId}:`, error);
                    }
                    return null;
                });

                const compsData = await Promise.all(compPromises);
                const validCompetitions = compsData.filter(comp => comp !== null);
                setCompetitions(validCompetitions);

                // Set first competition as active for timer
                if (validCompetitions?.length > 0) {
                    setCompetition(validCompetitions[0]);
                    await fetchClimbData(validCompetitions);
                }

            } catch (error) {
                console.error("Error fetching group data:", error);
            } finally {
                setIsLoading(false);
            }
        }

        fetchGroupData();
    }, [groupId, navigate, fetchClimbData]);

    // Setup polling intervals
    useEffect(() => {
        isMountedRef.current = true;

        // Initial fetch
        if (competition?._id) {
            fetchTimerState();
        }

        // Set up polling for timer state every 3 seconds
        const timerPolling = setInterval(() => {
            if (isMountedRef.current && competition?._id) {
                fetchTimerState();
            }
        }, 3000);

        // Set up polling for climb data every 5 seconds
        const climbPolling = setInterval(() => {
            if (isMountedRef.current && competitions.length > 0) {
                fetchClimbData(competitions);
            }
        }, 5000);

        pollingIntervalRef.current = timerPolling;
        climbPollingIntervalRef.current = climbPolling;

        return () => {
            isMountedRef.current = false;
            stopLocalTimer();

            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            if (climbPollingIntervalRef.current) {
                clearInterval(climbPollingIntervalRef.current);
            }
        };
    }, [competition, competitions, fetchTimerState, fetchClimbData, stopLocalTimer]);

    // Keep timer running if we have time left
    useEffect(() => {
        if (localRemainingTime > 0 && !timerIntervalRef.current) {
            const { status, currentPhase } = timerState || {};
            const shouldBeRunning = status === 'running' ||
                ['countdown', 'climbing', 'wrap_time', 'warning'].includes(currentPhase);

            if (shouldBeRunning) {
                console.log('⏱️ Timer has time but not running, starting it');
                startLocalTimer();
            }
        }
    }, [localRemainingTime, timerState, startLocalTimer]);

    // Update current round climbers when data changes
    useEffect(() => {
        const updateCurrentRoundClimbers = () => {
            if (!competitions.length || !Object.keys(allClimbData).length || !timerState) {
                setCurrentRoundClimbers([]);
                return;
            }

            const currentRoundToShow = timerState.currentRound || currentRound || 1;
            const allClimbers = [];

            // Get climbers from ALL competitions
            competitions.forEach(comp => {
                const boulders = comp.boulders || [];

                // For each boulder in current round, find assigned climber
                boulders.forEach(boulder => {
                    const climbDataForComp = allClimbData[comp._id] || [];
                    const climbsForBoulder = climbDataForComp.filter(climb =>
                        climb.boulderID === boulder._id &&
                        climb.round === currentRoundToShow
                    );

                    const climb = climbsForBoulder[0];
                    if (climb && climb.climberID) {
                        const attempts = climb.attempts || 0;
                        const zoneAttempt = climb.zoneAttempt || 0;
                        const topAttempt = climb.topAttempt || 0;
                        const zoneReached = climb.zoneReached || false;
                        const topReached = climb.topReached || false;
                        const points = calculateScore(climb, comp);

                        allClimbers.push({
                            competition: comp.name,
                            boulder: boulder.name,
                            climber: climb.climberID.name || 'Unknown',
                            bib: climb.climberID.bib || '?',
                            boulderColor: boulder.color || '#666',
                            climberId: climb.climberID._id,
                            competitionId: comp._id,
                            attempts: attempts,
                            zoneAttempt: zoneAttempt,
                            topAttempt: topAttempt,
                            zoneReached: zoneReached,
                            topReached: topReached,
                            points: points,
                            isCurrent: true
                        });
                    }
                });
            });

            setCurrentRoundClimbers(allClimbers);
        };

        const updateNextRoundClimbers = () => {
            if (!competitions.length || !Object.keys(allClimbData).length || !timerState) {
                setNextRoundClimbers([]);
                return;
            }

            const currentRoundToShow = timerState.currentRound + 1 || currentRound + 1 || 2;
            const allClimbers = [];

            // Get climbers from ALL competitions
            competitions.forEach(comp => {
                const boulders = comp.boulders || [];

                // For each boulder in current round, find assigned climber
                boulders.forEach(boulder => {
                    const climbDataForComp = allClimbData[comp._id] || [];
                    const climbsForBoulder = climbDataForComp.filter(climb =>
                        climb.boulderID === boulder._id &&
                        climb.round === currentRoundToShow
                    );

                    const climb = climbsForBoulder[0];
                    if (climb && climb.climberID) {
                        const attempts = climb.attempts || 0;
                        const zoneAttempt = climb.zoneAttempt || 0;
                        const topAttempt = climb.topAttempt || 0;
                        const zoneReached = climb.zoneReached || false;
                        const topReached = climb.topReached || false;
                        const points = calculateScore(climb, comp);

                        allClimbers.push({
                            competition: comp.name,
                            boulder: boulder.name,
                            climber: climb.climberID.name || 'Unknown',
                            bib: climb.climberID.bib || '?',
                            boulderColor: boulder.color || '#666',
                            climberId: climb.climberID._id,
                            competitionId: comp._id,
                            attempts: attempts,
                            zoneAttempt: zoneAttempt,
                            topAttempt: topAttempt,
                            zoneReached: zoneReached,
                            topReached: topReached,
                            points: points,
                            isCurrent: true
                        });
                    }
                });
            });

            setNextRoundClimbers(allClimbers);
        };

        updateCurrentRoundClimbers();
        updateNextRoundClimbers();
    }, [competitions, allClimbData, timerState, currentRound, calculateScore]);

    // Update current time every second
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Format time for display
    const formatTime = (seconds) => {
        if (!seconds && seconds !== 0) return '00:00';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Get last round results for ALL competitions
    const getLastRoundResultsForAllCompetitions = useCallback(() => {
        if (!competitions.length || !Object.keys(allClimbData).length || !timerState) return [];

        const roundToShow = timerState.currentRound || currentRound || 1;
        const allResults = [];

        // Get results for each competition
        competitions.forEach(comp => {
            const boulders = comp.boulders || [];
            const results = [];

            boulders.forEach(boulder => {
                const climbDataForComp = allClimbData[comp._id] || [];
                const climbsForBoulder = climbDataForComp.filter(climb =>
                    climb.boulderID === boulder._id &&
                    climb.round === roundToShow
                );

                const climb = climbsForBoulder[0];
                if (climb && climb.climberID) {
                    results.push({
                        competitionName: comp.name,
                        boulder: boulder.name,
                        climber: climb.climberID.name || 'Unknown',
                        bib: climb.climberID.bib || '?',
                        attemptsToTop: climb.topReached ? climb.topAttempt : null,
                        attemptsToZone: climb.zoneReached ? climb.zoneAttempt : null,
                        attempts: climb.attempts || 0,
                        topReached: climb.topReached || false,
                        zoneReached: climb.zoneReached || false,
                        points: calculateScore(climb, comp)
                    });
                } else {
                    results.push({
                        competitionName: comp.name,
                        boulder: boulder.name,
                        climber: '—',
                        bib: '—',
                        attemptsToTop: null,
                        attemptsToZone: null,
                        attempts: 0,
                        topReached: false,
                        zoneReached: false,
                        points: 0
                    });
                }
            });

            allResults.push({
                competition: comp,
                results: results
            });
        });

        return allResults;
    }, [competitions, allClimbData, calculateScore, timerState, currentRound]);

    // Get timer display info based on current phase
    const getTimerDisplayInfo = () => {
        if (!timerState || !timerState.status) {
            return {
                bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
                textColor: 'text-white',
                accentColor: 'text-slate-300',
                label: 'READY',
                showResults: false,
                showCurrentClimber: false,
                showNextRound: false,
                showCurrentRoundClimbers: false
            };
        }

        const { currentPhase, status } = timerState;
        const warningTime = competition?.warningTime || 60;
        const phase = status || currentPhase;

        switch (phase) {
            case 'countdown':
                return {
                    bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
                    textColor: 'text-white',
                    accentColor: 'text-slate-300',
                    label: 'OBSERVATION TIME',
                    showResults: false,
                    showCurrentClimber: false,
                    showNextRound: false,
                    showCurrentRoundClimbers: true
                };
            case 'climbing':
                if (localRemainingTime <= warningTime) {
                    return {
                        bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
                        textColor: 'text-yellow-300',
                        accentColor: 'text-slate-300',
                        label: 'CLIMB TIME — WARNING',
                        showResults: false,
                        showCurrentClimber: false,
                        showNextRound: false,
                        showCurrentRoundClimbers: true
                    };
                }
                return {
                    bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
                    textColor: 'text-white',
                    accentColor: 'text-slate-300',
                    label: 'CLIMB TIME',
                    showResults: false,
                    showCurrentClimber: false,
                    showNextRound: false,
                    showCurrentRoundClimbers: true
                };
            case 'wrap_time':
                return {
                    bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
                    textColor: 'text-yellow-300',
                    accentColor: 'text-slate-300',
                    label: 'WRAPPING — NEXT ROTATION STARTS IN',
                    showResults: true,
                    showCurrentClimber: false,
                    showNextRound: false,
                    showCurrentRoundClimbers: false
                };
            case 'warning':
                return {
                    bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
                    textColor: 'text-red-300',
                    accentColor: 'text-slate-300',
                    label: 'CLIMB TIME — WARNING',
                    showResults: false,
                    showCurrentClimber: false,
                    showNextRound: false,
                    showCurrentRoundClimbers: true
                };
            case 'completed':
                return {
                    bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
                    textColor: 'text-white',
                    accentColor: 'text-slate-300',
                    label: 'COMPETITION COMPLETED',
                    showResults: true,
                    showCurrentClimber: false,
                    showNextRound: false,
                    showCurrentRoundClimbers: false
                };
            case 'paused':
                return {
                    bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
                    textColor: 'text-white',
                    accentColor: 'text-slate-300',
                    label: 'COMPETITION PAUSED',
                    showResults: true,
                    showCurrentClimber: false,
                    showNextRound: false,
                    showCurrentRoundClimbers: false
                };
            default:
                return {
                    bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
                    textColor: 'text-white',
                    accentColor: 'text-slate-300',
                    label: 'READY',
                    showResults: false,
                    showCurrentClimber: false,
                    showNextRound: false,
                    showCurrentRoundClimbers: false
                };
        }
    };

    const timerInfo = getTimerDisplayInfo();
    const allCompetitionResults = getLastRoundResultsForAllCompetitions();

    if (!competition) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white mb-4"></div>
                    <h1 className="text-3xl text-white mb-2 font-bold">Loading Competition...</h1>
                    <p className="text-slate-400">Please wait while we load the timer.</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`min-h-screen bg-gradient-to-br ${timerInfo.bgGradient} flex flex-col p-4 md:p-8 transition-all duration-1000`}
            onClick={handleUserInteraction}
        >

            {/* Audio Manager Component */}
            <AudioManager
                timerState={timerState}
                localRemainingTime={localRemainingTime}
                competitionWarningTime={competition?.warningTime}
            />

            {/* Audio Enable Prompt */}
            {showAudioPrompt && (
                <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-pulse">
                    <div className="bg-yellow-600/90 backdrop-blur-sm px-6 py-4 rounded-lg shadow-xl border border-yellow-500/50">
                        <div className="flex items-center gap-3">
                            <VolumeX className="w-6 h-6 text-white" />
                            <div>
                                <h3 className="text-white font-bold text-lg">Click anywhere to enable audio</h3>
                                <p className="text-yellow-100 text-sm mt-1">
                                    Browsers require user interaction to play timer sounds
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Sync Status Indicator */}
            <div className="fixed top-4 right-4 z-50">
                <div className="px-4 py-2 rounded-lg text-sm font-semibold shadow-lg bg-green-600/90 text-white backdrop-blur-sm">
                    <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-white"></div>
                        SYNCED
                    </div>
                    {lastSyncTime && (
                        <div className="text-xs mt-1 opacity-75">
                            Last sync: {new Date(lastSyncTime).toLocaleTimeString()}
                        </div>
                    )}
                </div>
            </div>

            {/* Competition Group Info */}
            <div className="fixed top-4 left-4 z-50">
                <div className="bg-slate-800/90 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <p className="text-slate-300 text-sm">
                        {competitions.length} Competitions | Round {timerState?.currentRound || currentRound}
                    </p>
                </div>
            </div>

            {/* Timer Label */}
            <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 text-center">
                <div className={`${timerInfo.accentColor} text-1xl md:text-4xl lg:text-3xl tracking-[0.3em] uppercase font-bold`}>
                    {timerInfo.label}
                </div>
            </div>

            {/* Main Timer Display */}
            <div className="flex-grow flex flex-col justify-center items-center w-full max-w-7xl mx-auto">
                <div className="text-center space-y-4 md:space-y-6 w-full">
                    <div className={`${timerInfo.textColor} text-[12rem] sm:text-[16rem] md:text-[22rem] lg:text-[28rem] font-light tracking-tight leading-none ${timerInfo.pulse ? 'animate-pulse' : ''}`}>
                        {formatTime(localRemainingTime)}
                    </div>
                </div>
            </div>

            {/* COMPACT FOOTER: Two Competitions Side-by-Side */}
            {timerInfo.showCurrentRoundClimbers && currentRoundClimbers.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 bg-slate-900/80 backdrop-blur-sm border-t border-slate-700/50 py-1 z-40">
                    <div className="max-w-8xl mx-auto px-4">
                        {/* Two Competition Columns - SUPER COMPACT */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                            {competitions.slice(0, 2).map((competitionItem, compIndex) => {
                                // Get all boulders for this competition
                                const boulders = competitionItem.boulders || [];

                                // Get current round climbers for this competition
                                const compClimbers = currentRoundClimbers.filter(
                                    climber => climber.competitionId === competitionItem._id
                                );

                                // Get next round climbers for this competition
                                const compNextClimbers = nextRoundClimbers.filter(
                                    climber => climber.competitionId === competitionItem._id
                                );

                                // Create arrays for 4 boulder positions
                                const currentClimbersByBoulder = [];
                                const nextClimbersByBoulder = [];

                                // Fill the arrays with climbers in boulder order (B1, B2, B3, B4)
                                for (let i = 0; i < 4; i++) {
                                    const boulder = boulders[i];

                                    // Find current climber for this boulder
                                    const currentClimber = boulder ?
                                        compClimbers.find(climber => climber.boulder === boulder.name) :
                                        null;

                                    // Find next climber for this boulder
                                    const nextClimber = boulder ?
                                        compNextClimbers.find(climber => climber.boulder === boulder.name) :
                                        null;

                                    currentClimbersByBoulder[i] = currentClimber || {
                                        boulder: boulder?.name || `B${i + 1}`,
                                        climber: '—',
                                        bib: '—',
                                        attempts: 0,
                                        zoneAttempt: 0,
                                        topAttempt: 0,
                                        zoneReached: false,
                                        topReached: false,
                                        points: 0,
                                        isCurrent: false,
                                        boulderColor: boulder?.color || '#666'
                                    };

                                    nextClimbersByBoulder[i] = nextClimber || {
                                        boulder: boulder?.name || `B${i + 1}`,
                                        climber: '—',
                                        bib: '—',
                                        attempts: 0,
                                        zoneAttempt: 0,
                                        topAttempt: 0,
                                        zoneReached: false,
                                        topReached: false,
                                        points: 0,
                                        isCurrent: false,
                                        boulderColor: boulder?.color || '#666'
                                    };
                                }

                                return (
                                    <div key={competitionItem._id} className="rounded p-1.5">
                                        {/* Competition Header - EXTRA SMALL */}
                                        <div className="flex items-center justify-between mb-1">
                                            <h4 className="text-xs font-bold text-white truncate">
                                                {competitionItem.name}
                                            </h4>
                                            <div className="flex items-center gap-1">
                                                <div className="flex items-center gap-0.5">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-green-500"></div>
                                                    <span className="text-[9px] text-slate-300">Now</span>
                                                </div>
                                                <div className="flex items-center gap-0.5">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-slate-500"></div>
                                                    <span className="text-[9px] text-slate-300">Next</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="relative my-0.5">
                                            <div className="absolute inset-0 flex items-center">
                                                <div className="w-full border-t border-slate-700/30"></div>
                                            </div>
                                            <div className="relative flex justify-center">
                                                <span className="px-1 bg-slate-800 text-slate-400 text-[8px] rounded">
                                                    Current
                                                </span>
                                            </div>
                                        </div>
                                        {/* Current Climbers - Organized by Boulder */}
                                        <div className="mb-1">
                                            <div className="grid grid-cols-4 gap-0.5">
                                                {currentClimbersByBoulder.map((climber, index) => {
                                                    const attempts = climber.attempts || 0;
                                                    const zoneAttempt = climber.zoneAttempt || 0;
                                                    const topAttempt = climber.topAttempt || 0;
                                                    const zoneReached = climber.zoneReached || false;
                                                    const topReached = climber.topReached || false;
                                                    const points = climber.points || 0;
                                                    const zoneText = zoneReached ? `${zoneAttempt}-Z` : '';
                                                    const topText = topReached ? `${topAttempt}-T` : '';
                                                    const attemptsText = [zoneText, topText].filter(Boolean).join(', ');
                                                    const scoreText = points > 0 ? `: ${points.toFixed(1)}` : '';

                                                    return (
                                                        <div
                                                            key={`${climber.bib}-current-${index}`}
                                                            className="bg-slate-900/80 rounded border border-green-500/20 p-1 relative"
                                                        >
                                                            {/* Boulder indicator - small colored dot in top left */}
                                                            <div className="absolute top-0.5 left-0.5">
                                                                <div className="w-2 h-2 rounded-full bg-green-500 mr-0.5"></div>
                                                            </div>

                                                            <div className="flex items-start justify-between mb-0.5">
                                                                <div className="flex items-center gap-0.5">
                                                                    <div className="w-2 h-2 rounded-full bg-green-500 mr-0.5"></div>
                                                                </div>
                                                                <div className="text-[10px] font-semibold text-white truncate mb-0.5 text-center">
                                                                    #{climber.bib} {climber.climber !== '—' ? climber.climber.split(' ')[0] : '—'}
                                                                </div>
                                                                <span className="text-[10px] font-bold text-yellow-300">
                                                                    {attempts}<span className="text-[8px] text-yellow-400">★</span>
                                                                </span>
                                                            </div>
                                                            <div className="text-[8px] text-slate-300 text-center truncate leading-tight">
                                                                {attemptsText ? `${attemptsText} ${scoreText}` : '—'}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        {/* Divider */}
                                        <div className="relative my-0.5">
                                            <div className="absolute inset-0 flex items-center">
                                                <div className="w-full border-t border-slate-700/30"></div>
                                            </div>
                                            <div className="relative flex justify-center">
                                                <span className="px-1 bg-slate-800 text-slate-400 text-[8px] rounded">
                                                    NEXT
                                                </span>
                                            </div>
                                        </div>
                                        {/* Next Climbers - Organized by Boulder */}
                                        <div>
                                            <div className="grid grid-cols-4 gap-0.5">
                                                {nextClimbersByBoulder.map((climber, index) => {
                                                    const climbDataForComp = allClimbData[competitionItem._id] || [];
                                                    const boulder = boulders[index];

                                                    // If climber is a placeholder ("—"), show empty slot
                                                    if (climber.climber === '—') {
                                                        return (
                                                            <div
                                                                key={`empty-next-${index}`}
                                                                className="bg-slate-900/20 rounded border border-slate-700/10 border-dashed p-1 flex flex-col items-center justify-center relative"
                                                            >
                                                                {/* Boulder indicator */}
                                                                <div className="absolute top-0.5 left-0.5">
                                                                    <div
                                                                        className="w-1.5 h-1.5 rounded-full"
                                                                        style={{ backgroundColor: climber.boulderColor }}
                                                                    ></div>
                                                                </div>
                                                                <span className="text-[8px] text-slate-600">—</span>
                                                            </div>
                                                        );
                                                    }

                                                    // Find previous climb for stats
                                                    const prevClimb = climbDataForComp.find(c =>
                                                        c.climberID?._id === climber.climberId &&
                                                        c.round === ((timerState?.currentRound || currentRound) - 1)
                                                    );
                                                    const prevAttempts = prevClimb?.attempts || 0;
                                                    const prevZoneAttempt = prevClimb?.zoneAttempt || 0;
                                                    const prevTopAttempt = prevClimb?.topAttempt || 0;
                                                    const prevZoneReached = prevClimb?.zoneReached || false;
                                                    const prevTopReached = prevClimb?.topReached || false;
                                                    const prevPoints = prevClimb ? calculateScore(prevClimb, competitionItem) : 0;
                                                    const prevZoneText = prevZoneReached ? `${prevZoneAttempt}-Z` : '';
                                                    const prevTopText = prevTopReached ? `${prevTopAttempt}-T` : '';
                                                    const prevAttemptsText = [prevZoneText, prevTopText].filter(Boolean).join(', ');
                                                    const prevScoreText = prevPoints > 0 ? `: ${prevPoints.toFixed(1)}` : '';

                                                    return (
                                                        <div
                                                            key={`${climber.bib}-next-${index}`}
                                                            className="bg-slate-900/60 rounded border border-slate-700/30 p-1 relative"
                                                        >
                                                            {/* Boulder indicator */}
                                                            <div className="absolute top-0.5 left-0.5">
                                                                <div className="w-1.5 h-1.5 rounded-full bg-slate-500"></div>
                                                            </div>

                                                            <div className="flex items-start justify-between mb-0.5">
                                                                <div className="flex items-center gap-0.5">
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-slate-500"></div>
                                                                </div>
                                                                <div className="text-[10px] font-semibold text-white truncate mb-0.5 text-center">
                                                                    #{climber.bib} {climber.climber.split(' ')[0]}
                                                                </div>
                                                                <span className="text-[10px] font-bold text-yellow-300">
                                                                    0<span className="text-[8px] text-yellow-400">★</span>
                                                                </span>
                                                            </div>
                                                            <div className="text-[8px] text-slate-400 text-center truncate leading-tight">
                                                                {prevAttemptsText ? `${prevAttemptsText} ${prevScoreText}` : '—'}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Boulder Legend */}
                                        <div className="mt-1 flex items-center justify-center gap-2">
                                            {boulders.slice(0, 4).map((boulder, idx) => (
                                                <div key={idx} className="flex items-center gap-0.5">
                                                    <div
                                                        className="w-2 h-2 rounded-full"
                                                        style={{ backgroundColor: boulder?.color || '#666' }}
                                                    ></div>
                                                    <span className="text-[7px] text-slate-400">
                                                        {boulder?.name || `B${idx + 1}`}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Multiple Competition Results Tables for Wrap Time */}
            {timerInfo.showResults && allCompetitionResults.length > 0 && (
                <div className="mt-auto pt-8 pb-4 max-w-full mx-auto w-full">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                        {allCompetitionResults.map((compData, compIndex) => (
                            <div key={compData.competition._id} className="bg-slate-800/50 backdrop-blur-sm rounded-lg overflow-hidden border border-slate-700">
                                <div className="bg-slate-700/80 p-4 border-b border-slate-700">
                                    <h3 className="text-xl md:text-2xl font-bold text-white text-center">
                                        {compData.competition.name}
                                    </h3>
                                    <p className="text-slate-300 text-center text-sm mt-1">
                                        Zone: {compData.competition.zonepoints || 10}pts |
                                        Top: {compData.competition.toppoints || 25}pts
                                    </p>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full min-w-[400px]">
                                        <thead>
                                            <tr className="border-b border-slate-700 text-slate-300 text-xs md:text-sm">
                                                <th className="text-left p-3 md:p-4 font-light">BOULDER</th>
                                                <th className="text-left p-3 md:p-4 font-light">CLIMBER</th>
                                                <th className="text-center p-3 md:p-4 font-light">TOP</th>
                                                <th className="text-center p-3 md:p-4 font-light">ZONE</th>
                                                <th className="text-center p-3 md:p-4 font-light">POINTS</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {compData.results.map((result, index) => (
                                                <tr key={index} className="border-b border-slate-700/50 text-white hover:bg-slate-700/30 transition-colors">
                                                    <td className="p-3 md:p-4 text-lg md:text-xl font-bold">{result.boulder}</td>
                                                    <td className="p-3 md:p-4 text-sm md:text-base">
                                                        <span className="text-slate-400">#{result.bib}</span> {result.climber}
                                                    </td>
                                                    <td className="text-center p-3 md:p-4">
                                                        <div className="flex flex-col items-center">
                                                            {result.topReached ? (
                                                                <>
                                                                    <CheckCircle2 className="w-5 h-5 text-green-400 mb-1" />
                                                                    <span className="text-sm font-light">{result.attemptsToTop}</span>
                                                                </>
                                                            ) : (
                                                                <span className="text-lg text-slate-600">—</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="text-center p-3 md:p-4">
                                                        <div className="flex flex-col items-center">
                                                            {result.zoneReached ? (
                                                                <>
                                                                    <CheckCircle2 className="w-5 h-5 text-yellow-400 mb-1" />
                                                                    <span className="text-sm font-light">{result.attemptsToZone}</span>
                                                                </>
                                                            ) : (
                                                                <span className="text-lg text-slate-600">—</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-3 md:p-4 text-lg md:text-xl font-bold text-green-400 text-center">
                                                        {result.points.toFixed(1)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="bg-slate-700/50 text-white">
                                                <td colSpan="4" className="p-3 md:p-4 text-right font-bold">
                                                    Total Points:
                                                </td>
                                                <td className="p-3 md:p-4 text-center text-xl font-bold text-green-300">
                                                    {compData.results.reduce((sum, result) => sum + result.points, 0).toFixed(1)}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};